import { supabase } from './supabaseClient';

/* ── Module-level cache ──────────────────────────────── */
let cachedSpinWheelEnabled: boolean | null = null;
let fetchPromise: Promise<boolean> | null = null;
let listeners: Array<(v: boolean) => void> = [];

function notify(value: boolean) {
  for (const fn of listeners) fn(value);
}

export function subscribeSpinWheel(fn: (v: boolean) => void) {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((f) => f !== fn);
  };
}

/* ── Read ─────────────────────────────────────────────── */
export async function fetchSpinWheelEnabled(): Promise<boolean> {
  if (cachedSpinWheelEnabled !== null) return cachedSpinWheelEnabled;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'spin_wheel_enabled')
        .single();

      if (error || !data) {
        cachedSpinWheelEnabled = true; // default: enabled
        return true;
      }
      cachedSpinWheelEnabled = (data as any).value === true;
      return cachedSpinWheelEnabled;
    } catch {
      cachedSpinWheelEnabled = true;
      return true;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/* ── Write (admin only) ──────────────────────────────── */
export async function setSpinWheelEnabled(enabled: boolean): Promise<void> {
  const { error } = await supabase
    .from('site_settings')
    .upsert({ key: 'spin_wheel_enabled', value: enabled }, { onConflict: 'key' });

  if (error) throw new Error(error.message);
  cachedSpinWheelEnabled = enabled;
  notify(enabled);
}

/* ── Cache helpers ────────────────────────────────────── */
export function invalidateSpinWheelCache() {
  cachedSpinWheelEnabled = null;
  fetchPromise = null;
}
